import type { SessionOrg } from '@/types/session.types';
import { sanitizeContactUsHtml } from '@/utils/whiteLabeling/sanitizeContactUsHtml';

export const CONTACT_US_URL_FORMAT_MESSAGE =
  'Use an https or http link, a path starting with / (for example /help/contact), or leave blank for the default Hub contact form.';

// Suggested max for contactUsCustomHtml before JSON overhead.
export const CONTACT_US_CUSTOM_HTML_MAX_LENGTH = 3000;

type OrgWelcomeSource =
  | Pick<SessionOrg, 'whiteLabelingConfig' | 'welcomeMessage'>
  | null
  | undefined;

export const HOME_PAGE_CARD_CONFIG = [
  {
    key: 'showCornerstoneIndex',
    label: 'Cornerstone Index',
  },
  { key: 'showNews', label: 'News' },
  { key: 'showResources', label: 'Resources' },
  {
    key: 'showImportantDates',
    label: 'Important Dates',
  },
] as const;

export type HomePageCardConfig = (typeof HOME_PAGE_CARD_CONFIG)[number];
export type HomePageCardVisibilityKey = HomePageCardConfig['key'];

export const HOME_PAGE_CARD_VISIBILITY_KEYS = HOME_PAGE_CARD_CONFIG.map(
  (card) => card.key
) as HomePageCardVisibilityKey[];

export type WhiteLabelingConfig = Partial<
  Record<HomePageCardVisibilityKey, boolean>
> & {
  showHome?: boolean;
  welcomeMessage?: string | null;
  showContactUs?: boolean;
  showReleaseNotes?: boolean;
  showGiveFeedback?: boolean;
  contactUsUrl?: string | null;
  contactUsCustomHtml?: string | null;
};

/** Customer-facing labels for activity logs (match Site Configuration UI). */
export const WHITE_LABELING_ACTIVITY_LOG_FIELD_LABELS: Record<
  keyof WhiteLabelingConfig,
  string
> = {
  showHome: 'Home page',
  welcomeMessage: 'Welcome Message',
  showCornerstoneIndex: 'Cornerstone Index',
  showNews: 'News',
  showResources: 'Resources',
  showImportantDates: 'Important Dates',
  showContactUs: 'Contact Us',
  showReleaseNotes: 'Release Notes',
  showGiveFeedback: 'Give Feedback',
  contactUsUrl: 'Custom Contact URL',
  contactUsCustomHtml: 'Contact Us HTML',
};

export function getWhiteLabelingActivityLogFieldLabel(
  key: string
): string | undefined {
  return WHITE_LABELING_ACTIVITY_LOG_FIELD_LABELS[
    key as keyof WhiteLabelingConfig
  ];
}

type OrgLike =
  | Pick<SessionOrg, 'allowSiteWhiteLabeling' | 'whiteLabelingConfig'>
  | null
  | undefined;

type OrgContactHtmlSource =
  | Pick<SessionOrg, 'whiteLabelingConfig'>
  | null
  | undefined;

function trimToNull(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

export function parseWhiteLabelingConfig(
  raw: string | null | undefined
): WhiteLabelingConfig {
  if (!raw) return {};

  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as WhiteLabelingConfig;
    }
    return {};
  } catch {
    return {};
  }
}

export function serializeWhiteLabelingConfig(cfg: WhiteLabelingConfig): string {
  return JSON.stringify(cfg ?? {});
}

export const persistWelcomeMessage = trimToNull;

export function resolveWelcomeMessageFromOrg(
  org: OrgWelcomeSource
): string | null {
  const cfg = parseWhiteLabelingConfig(org?.whiteLabelingConfig);
  if (Object.prototype.hasOwnProperty.call(cfg, 'welcomeMessage')) {
    return persistWelcomeMessage(cfg.welcomeMessage);
  }
  return persistWelcomeMessage(org?.welcomeMessage);
}

/**
 * For boolean visibility flags, treat any value other than explicit `false` as
 * shown. Default = visible. White-labeling-off orgs always fall back to default.
 */
function isShown(org: OrgLike, key: keyof WhiteLabelingConfig): boolean {
  if (!org?.allowSiteWhiteLabeling) return true;
  const cfg = parseWhiteLabelingConfig(org.whiteLabelingConfig);
  return cfg[key] !== false;
}

export function effectiveShowHome(org: OrgLike): boolean {
  return isShown(org, 'showHome');
}

export function effectiveShowHomeCard(
  org: OrgLike,
  key: HomePageCardVisibilityKey
): boolean {
  return isShown(org, key);
}

export function getVisibleHomePageCards(org: OrgLike): HomePageCardConfig[] {
  return HOME_PAGE_CARD_CONFIG.filter((card) =>
    effectiveShowHomeCard(org, card.key)
  );
}

export function effectiveShowContactUs(org: OrgLike): boolean {
  return isShown(org, 'showContactUs');
}

export function effectiveShowReleaseNotes(org: OrgLike): boolean {
  return isShown(org, 'showReleaseNotes');
}

export function effectiveShowGiveFeedback(org: OrgLike): boolean {
  return isShown(org, 'showGiveFeedback');
}

export const persistContactUsUrl = trimToNull;

export const persistContactUsCustomHtml = trimToNull;

/** Sanitize then persist — shared by API Joi and admin save flows. */
export function normalizeContactUsCustomHtmlForSave(
  html: string | null | undefined
): string | null {
  return persistContactUsCustomHtml(sanitizeContactUsHtml(html));
}

export function resolveContactUsCustomHtmlFromOrg(
  org: OrgContactHtmlSource
): string | null {
  const cfg = parseWhiteLabelingConfig(org?.whiteLabelingConfig);
  return persistContactUsCustomHtml(cfg.contactUsCustomHtml);
}

/**
 * Returns a user-facing validation error for the Custom Contact URL field, or
 * `null` when the value is empty (default modal) or acceptable for save.
 */
export function getContactUsUrlValidationError(
  url: string | null | undefined
): string | null {
  const normalized = persistContactUsUrl(url);
  if (normalized === null) {
    return null;
  }

  const lower = normalized.toLowerCase();
  if (
    lower.startsWith('javascript:') ||
    lower.startsWith('data:') ||
    lower.startsWith('vbscript:')
  ) {
    return CONTACT_US_URL_FORMAT_MESSAGE;
  }
  if (normalized.startsWith('/')) {
    if (normalized.startsWith('//')) {
      return CONTACT_US_URL_FORMAT_MESSAGE;
    }
    return null;
  }
  try {
    const u = new URL(normalized);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') {
      return CONTACT_US_URL_FORMAT_MESSAGE;
    }
    return null;
  } catch {
    return CONTACT_US_URL_FORMAT_MESSAGE;
  }
}
